import { useEffect, useRef, useState } from 'react';
import type { ChartDot } from './chart-data';
import type { MarkingSide } from './marking-flow';

export type MarkingState = { dots: ChartDot[]; side: MarkingSide | null; automaticBuy: boolean };
export const emptyMarking = (): MarkingState => ({ dots: [], side: null, automaticBuy: false });
type Timeline = { current: MarkingState; past: MarkingState[]; future: MarkingState[] };
const copy = (state: MarkingState): MarkingState => ({ ...state, dots: state.dots.map(dot => ({ ...dot })) });
const key = (state: MarkingState) => JSON.stringify(state);

// Record the complete committed React state, once per action. Direction and the
// automatic second-click state travel with the points, including partial BUYs.
export function useMarkingHistory(scope: string, state: MarkingState, restore: (state: MarkingState) => void) {
  const timelines = useRef(new Map<string, Timeline>());
  const [, render] = useState(0);
  if (!timelines.current.has(scope)) timelines.current.set(scope, { current: copy(state), past: [], future: [] });
  const timeline = timelines.current.get(scope)!;
  const serialized = key(state);
  useEffect(() => {
    if (key(timeline.current) === serialized) return;
    timeline.past = [...timeline.past.slice(-49), copy(timeline.current)];
    timeline.current = copy(state);
    timeline.future = [];
    render(n => n + 1);
  }, [timeline, serialized]);
  function travel(direction: 'past' | 'future') {
    const target = timeline[direction].at(-1);
    if (!target) return null;
    const other = direction === 'past' ? 'future' : 'past';
    timeline[other].push(copy(timeline.current));
    timeline[direction].pop();
    timeline.current = copy(target);
    restore(copy(target));
    render(n => n + 1);
    return target;
  }
  return { canUndo: timeline.past.length > 0, canRedo: timeline.future.length > 0, undo: () => travel('past'), redo: () => travel('future') };
}
